import React, { useEffect, MutableRefObject, useMemo, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { DraggableEventHandler } from 'react-draggable';
import { animateScroll as scroll } from 'react-scroll';
import { useScroll } from 'react-use';
import { Box, Skeleton, useTheme } from '@mui/material';

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import Disclosure from 'components/Icons/Disclosure';
import useCustomError from 'hooks/useCustomError';
import { useMaxWidthTabletLarge, useMinWidthDesktopMedium } from 'hooks/useMediaQueries';
import { useUserPermissionStatus } from 'selectors/user';
import type { GraphDataError } from 'types/graphs';
import type { SurfHeight } from 'types/units';
import type { DaySelectorData, DaySelectorOverview } from 'types/daySelector';
import type { SubregionDaySummaryData } from 'types/subregion';
import { useForecastDaysAllowed } from 'utils/forecastDaysAllowed';
import DaySelectorContent from './components/DaySelectorContent';

import styles from './ForecastDaySelector.module.scss';

export interface DaySelectorProps {
  activeRef?: MutableRefObject<HTMLButtonElement | null>;
  currentPosition?: { xRate: number; yRate: number };
  data?: DaySelectorData;
  daysAllowed?: number;
  daysPerRange?: number;
  daySummary: SubregionDaySummaryData;
  daysToDisplay?: Array<number>;
  isHistoricView: boolean;
  onDragDaySelectorStop?: DraggableEventHandler;
  onScreenResize?: () => void;
  paywall?: {
    isActive?: boolean;
    referrer?: string;
    segmentProperties: {
      category: string;
      pageName: string;
      spotId: string;
      subregionId: string;
      location: string;
    };
  };
  setDaysToDisplay: (startDayIndex: number) => void;
  chartIdPrefix?: string;
  isTableViewMode: boolean;
}

export const DaySelector: React.FC<DaySelectorProps> = ({
  activeRef,
  currentPosition,
  data,
  daysAllowed = 5,
  daysPerRange,
  daySummary,
  daysToDisplay,
  isHistoricView,
  onDragDaySelectorStop,
  onScreenResize,
  paywall,
  setDaysToDisplay,
  chartIdPrefix,
  isTableViewMode,
}) => {
  const GRAPH_HEIGHT = 114;

  const units = data?.associated?.units.waveHeight as SurfHeight;
  const spotData: DaySelectorOverview | undefined = data?.data.overview?.[0];
  const theme = useTheme();
  const isMobileView = useMaxWidthTabletLarge();
  const isDesktop = useMinWidthDesktopMedium();
  const activeDays = useMemo(
    () => (isTableViewMode ? [] : daysToDisplay),
    [daysToDisplay, isTableViewMode],
  );
  const scrollArea = useRef<HTMLDivElement | null>(null);
  const { x: scrollAreaX } = useScroll(scrollArea);
  useEffect(() => {
    if (onScreenResize) {
      onScreenResize();
    }
  }, [daysPerRange, onScreenResize]);
  const { hasCoreForecastPermissions } = useUserPermissionStatus();

  const handlePreviousClick = useCallback(() => {
    if (isTableViewMode) {
      scroll.scrollTo(0, {
        duration: 500,
        smooth: true,
        horizontal: true,
        containerId: scrollArea?.current?.id,
      });
    } else {
      setDaysToDisplay(activeDays?.[0] ? activeDays[0] - 1 : 0);
      activeRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [activeRef, activeDays, isTableViewMode, setDaysToDisplay]);

  const handleNextClick = useCallback(() => {
    if (isTableViewMode) {
      const scrollLeft =
        (scrollArea?.current?.scrollWidth ?? 0) - (scrollArea?.current?.clientWidth ?? 0);
      scroll.scrollTo(scrollLeft, {
        duration: 500,
        smooth: true,
        horizontal: true,
        containerId: scrollArea?.current?.id,
      });
    } else {
      setDaysToDisplay((activeDays?.[activeDays.length - 1] || 0) + 1);
      activeRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [activeDays, activeRef, isTableViewMode, setDaysToDisplay]);

  const selectorAtBeginning = useMemo(
    () => (isTableViewMode ? scrollAreaX === 0 : !activeDays?.[0]),
    [activeDays, isTableViewMode, scrollAreaX],
  );

  const { forecastDaysAllowed } = useForecastDaysAllowed({
    type: 'spot',
    isEntitled: hasCoreForecastPermissions,
  });

  const selectorAtEnd = useMemo(() => {
    if (isTableViewMode) {
      return (
        scrollAreaX ===
        (scrollArea?.current?.scrollWidth ?? 0) - (scrollArea?.current?.clientWidth ?? 0)
      );
    }
    return (activeDays?.[activeDays.length - 1] || 0) + 1 >= forecastDaysAllowed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDays, hasCoreForecastPermissions, isTableViewMode, scrollArea.current, scrollAreaX]);

  const showPrevNextArrows = !isMobileView && !(isTableViewMode && isDesktop);

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.metricUnits]: units === 'M',
      })}
      data-testid="day-selector-graph"
      id="day-selector-graph"
      ref={scrollArea}
    >
      {showPrevNextArrows && (
        <div
          className={classNames([styles.daySelectorPrevNextButton, styles.prev])}
          data-testid="day-selector-inline-prev-button"
        >
          <Disclosure
            ariaLabel="previous"
            color={
              selectorAtBeginning ? theme.palette.text.light : theme.palette.text.brandSecondary
            }
            disabled={selectorAtBeginning}
            onClick={handlePreviousClick}
            rotation={270}
            size={11}
          />
        </div>
      )}
      <div
        key={`graph-${spotData?.spotId}`}
        className={styles.graphRow}
        data-testid="day-selector-graph-spot"
      >
        {spotData?.surf && units && (
          <DaySelectorContent
            activeRef={activeRef}
            currentPosition={currentPosition}
            days={spotData?.days}
            daysAllowed={daysAllowed}
            daysPerRange={daysPerRange}
            daySummary={daySummary}
            daysToDisplay={activeDays}
            isHistoricView={isHistoricView}
            graphHeight={GRAPH_HEIGHT}
            onDragDaySelectorStop={onDragDaySelectorStop}
            paywall={paywall}
            setDaysToDisplay={setDaysToDisplay}
            spotId={spotData?.spotId ?? ''}
            surfData={spotData?.surf}
            units={units}
            chartIdPrefix={chartIdPrefix}
            isTableViewMode={isTableViewMode}
          />
        )}
        {!!spotData?.error && (
          <div className={styles.spotErrorWrapper} data-testid="day-selector-graph-spot-error">
            <ErrorMessage />
          </div>
        )}
      </div>
      {showPrevNextArrows && (
        <div
          className={classNames(styles.daySelectorPrevNextButton, styles.next)}
          data-testid="day-selector-inline-next-button"
        >
          <Disclosure
            ariaLabel="next"
            color={selectorAtEnd ? theme.palette.text.light : theme.palette.text.brandSecondary}
            disabled={selectorAtEnd}
            onClick={handleNextClick}
            rotation={90}
            size={11}
          />
        </div>
      )}
    </div>
  );
};

interface LoaderProps extends DaySelectorProps {
  loading?: boolean;
}

// need to use seperate component due to useScroll always need the ref to exist at the root of the component
// cannot toggle the ref on and off when loading or useScroll will not work
const Loader: React.FC<LoaderProps> = (props) => {
  const GRAPH_HEIGHT = 114;

  const { loading } = props;

  return loading ? (
    <Skeleton
      data-testid="day-selector-graph-loading"
      variant="rectangular"
      width="100%"
      height={GRAPH_HEIGHT}
    />
  ) : (
    <DaySelector {...props} />
  );
};

interface Props extends LoaderProps {
  error?: GraphDataError;
}

const ForecastDaySelector: React.FC<Props> = (props) => {
  const { error } = props;
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error });

  return (
    <ErrorBoundary
      error={hasError}
      errorBoundaryId="forecast-day-selector"
      render={() => (
        <Box data-testid="day-selector-graph-error">
          <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
        </Box>
      )}
    >
      <Loader {...props} />
    </ErrorBoundary>
  );
};

export default ForecastDaySelector;
